import StaticText from './Element/StaticText'
import SliderGroup from './Element/SliderGroup'
import Slider from './Element/Slider'
import Selection from './Element/Selection/Selection'
import DynamicText from './Element/DynamicText'
import { Element, Type } from '../../../../types/Survey/Element'
import { Survey } from '../../../../types/Survey/Survey'
import { Answer } from '../../../../types/Survey/Answer/Answer'
import { Submit, UpdateElement, UpdateDayNumber, UpdateOrganizationUnitUUID } from '../../../hooks/useSurveyAnswer/useSurveyAnswer'
import { motion } from 'framer-motion'
import { useState } from 'react'
import Navigation from './Navigation/Navigation'
import MediumContainer from '../../../UI/Container/MediumContainer'
import DaySelection from './Element/DaySelection'
import OrganizationUnitSelection from './Element/OrganizationUnitSelection'
import OrganizationUnitCooperationSelection from './Element/OrganizationUnitCooperationSelection/OrganizationUnitCooperationSelection'
import SliderDropdownGroup from './Element/SliderDropdownGroup'
import SliderDropdown from './Element/SliderDropdown'
import BooleanSelection from './Element/BooleanSelection'
import NumberInput from './Element/NumberInput'
import DynamicNumberDescriptionAmountInput from './Element/DynamicNumberDescriptionAmountInput'
import AmountToggleInput from './Element/AmountToggleInput'
import OrganizationUnitNumberInput from './Element/OrganizationUnitNumberInput'
import DynamicOptionsWithNumberAndSelection from './Element/DynamicOptionsWithNumberAndSelection'

type Props = {
  survey: Survey
  answer: Answer
  updateElement: UpdateElement
  updateOrganizationUnitUUID: UpdateOrganizationUnitUUID
  updateDayNumber: UpdateDayNumber
  submit: Submit
}

const ElementList: React.FC<Props> = ({ survey, answer, updateElement, updateDayNumber, updateOrganizationUnitUUID, submit }) => {
  const [element, setElement] = useState<Element>(null)

  const getElement = <T,>(answerUUID: string): T => {
    return answer.elements.find((a) => a.uuid === answerUUID) as T
  }

  const variants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  }

  return (
    <div className="flex flex-col pb-20 pt-5 md:pt-10">
      {!!element && (
        <MediumContainer>
          <div className="min-h-[50vh] flex justify-center items-center">
            <motion.div
              key={element.uuid}
              className="relative border-opacity-20 md:px-5 py-5 gap-3 bg-white min-w-[50%]"
              initial="initial"
              animate="animate"
              exit="exit"
              variants={variants}
              transition={{ duration: 0.5 }}
            >
              <div className="p-5">
                {element.type === Type.DAY_SELECTION && (
                  <DaySelection survey={survey} element={element} answer={answer} updateElement={updateDayNumber} />
                )}
                {element.type === Type.ORGANIZATION_UNIT_SELECTION && (
                  <OrganizationUnitSelection survey={survey} element={element} answer={answer} updateElement={updateOrganizationUnitUUID} />
                )}
                {element.type === Type.ORGANIZATION_UNIT_COOPERATION_SELECTION && (
                  <OrganizationUnitCooperationSelection
                    survey={survey}
                    element={element}
                    answer={answer}
                    answerElement={getElement(element.uuid)}
                    updateElement={updateElement(element.uuid)}
                  />
                )}
                {element.type === Type.STATIC_TEXT && <StaticText element={element} />}
                {element.type === Type.SLIDER_GROUP && (
                  <SliderGroup element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.SLIDER && (
                  <Slider element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.SELECTION && (
                  <Selection element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.DYNAMIC_TEXT && (
                  <DynamicText element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.SLIDER_DROPDOWN_GROUP && (
                  <SliderDropdownGroup element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.SLIDER_DROPDOWN && (
                  <SliderDropdown element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.BOOLEAN_SELECTION && (
                  <BooleanSelection element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.NUMBER_INPUT && (
                  <NumberInput element={element} answerElement={getElement(element.uuid)} updateElement={updateElement(element.uuid)} />
                )}
                {element.type === Type.DYNAMIC_NUMBER_DESCRIPTION_AMOUNT_INPUT && (
                  <DynamicNumberDescriptionAmountInput
                    element={element}
                    answerElement={getElement(element.uuid)}
                    updateElement={updateElement(element.uuid)}
                  />
                )}
                {element.type === Type.AMOUNT_TOGGLE_INPUT && (
                  <AmountToggleInput
                    survey={survey}
                    element={element}
                    answerElement={getElement(element.uuid)}
                    updateElement={updateElement(element.uuid)}
                    answer={answer}
                  />
                )}
                {element.type === Type.ORGANIZATION_UNIT_NUMBER_INPUT && (
                  <OrganizationUnitNumberInput
                    survey={survey}
                    element={element}
                    answerElement={getElement(element.uuid)}
                    updateElement={updateElement(element.uuid)}
                  />
                )}
                {element.type === Type.DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION && (
                  <DynamicOptionsWithNumberAndSelection
                    element={element}
                    answerElement={getElement(element.uuid)}
                    updateElement={updateElement(element.uuid)}
                  />
                )}
              </div>
            </motion.div>
          </div>
        </MediumContainer>
      )}
      <Navigation survey={survey} currentElement={element} setCurrentElement={setElement} answer={answer} submit={submit} />
    </div>
  )
}

export default ElementList
